import { useCallback } from 'react';
import { ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions'; // assuming you've set up Firebase in this file
import { functions, storage } from '../firebase';
import { useDropzone } from 'react-dropzone';
import { useStoreState } from 'pullstate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CircularProgress } from '@mui/material';

function UploadDoc({
    CurrentScan,
    onFileUploaded,
    switchToTextTab,
    uploadComplete,
    onUploadComplete,
}) {
    const { loading } = useStoreState(CurrentScan);
    const maxSize = 10485760; // 10MB in bytes

    const switchTabAfterUpload = () => {
        if (onUploadComplete) {
            setTimeout(switchToTextTab, 2000); // 3 seconds delay
        }
    };

    const handleUpload = useCallback(
        async (file) => {
            // Indicate loading in the CurrentScan store
            CurrentScan.update((s) => {
                s.loading = true;
            });
            onUploadComplete(false);
            // Determine file type, handle iCloud case, and map file extension to MIME type
            let type = file.type;
            if (!type) {
                const extension = file.name.split('.').pop().toLowerCase();
                if (extension === 'icloud') {
                    console.error('Please download the file from iCloud before uploading.');
                    alert('Please download the file from iCloud before uploading.');
                    return;
                }

                const extensionToMime = {
                    doc: 'application/msword',
                    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    pdf: 'application/pdf',
                };

                type = extensionToMime[extension] || null;
            }

            // If file type is still undefined, alert user and exit
            if (!type) {
                alert('Unrecognized file extension');
                CurrentScan.update((s) => {
                    s.loading = false;
                });
                return;
            }

            // Create a reference in Firebase storage and start the upload
            const storageRef = ref(storage, file.name);
            const uploadTask = uploadBytesResumable(storageRef, file);

            try {
                // Wait for the upload to complete
                await uploadTask;

                // Process the uploaded file
                const processFile = httpsCallable(functions, 'processFile');
                const result = await processFile({
                    path: file.name,
                    contentType: type,
                });

                // Handle the result of file processing
                if (result.data && result.data.text) {
                    onFileUploaded(result.data.text); // Pass the extracted text to the parent component
                    onUploadComplete(true); // Indicate successful upload
                    switchTabAfterUpload();
                } else {
                    onUploadComplete(false); // Indicate unsuccessful processing
                }
            } catch (error) {
                // Handle errors during upload or processing
                console.error('Error during upload or processing:', error);
                alert(
                    'Error during upload or processing: Please make sure your file type is .doc, .docx, or .pdf'
                );
                onUploadComplete(false);
            } finally {
                // Reset loading status in either case
                CurrentScan.update((s) => {
                    s.loading = false;
                });
            }
        },
        [onFileUploaded, CurrentScan, onUploadComplete, switchToTextTab]
    );

    const onDrop = useCallback(
        (acceptedFiles, fileRejections) => {
            if (fileRejections.length > 0) {
                alert(
                    'This file was not accepted. Please make sure it is a .doc, .docx, or .pdf. The maximum file size is 10MB.'
                );
            } else {
                handleUpload(acceptedFiles[0]);
            }
        },
        [handleUpload]
    );
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                '.doc',
                '.docx',
            ],
            'application/pdf': ['.pdf'],
        },
        maxSize,
        //@ts-ignore
        className: 'drop-zone active',
    });

    return (
        <>
            <div
                {...getRootProps()}
                style={{
                    height: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    border: '2px dashed #ccc',
                }}
                className={`drop-zone ${isDragActive ? 'active' : ''}`}
            >
                <input {...getInputProps()} />
                {isDragActive ? (
                    <p>Drop the files here ...</p>
                ) : loading ? (
                    <CircularProgress />
                ) : uploadComplete ? (
                    <div>
                        <CheckCircleIcon color='success' />
                        <p>File uploaded successfully!</p>
                    </div>
                ) : (
                    <>
                        <p>Drag & drop .doc, .docx or .pdf files here, or click to select file</p>
                        <small>(10mb max file size)</small>
                    </>
                )}
            </div>
            <div />
        </>
    );
}

export default UploadDoc;
